import { useState, useEffect } from 'react'
import { Container, Row, Col, Card, Button, Form, Alert, ListGroup, Badge, Spinner, ProgressBar } from 'react-bootstrap'
import { useProfile } from '../contexts/ProfileContext'
import profileService from '../services/profileService'
import trustListService from '../services/trustListService'

function CopyTrust() {
  const { profiles } = useProfile()

  const [sourceProfileId, setSourceProfileId] = useState('')
  const [targetProfileId, setTargetProfileId] = useState('')
  const [sourceTrustRelationships, setSourceTrustRelationships] = useState([])
  const [targetTrustRelationships, setTargetTrustRelationships] = useState([])
  const [selectedItems, setSelectedItems] = useState(new Set())
  const [duplicateHandling, setDuplicateHandling] = useState('skip')

  const [loadingSource, setLoadingSource] = useState(false)
  const [loadingTarget, setLoadingTarget] = useState(false)
  const [copying, setCopying] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [copyResult, setCopyResult] = useState(null)

  // Load source trust relationships when source profile changes
  useEffect(() => {
    if (sourceProfileId) {
      loadSourceTrust()
    } else {
      setSourceTrustRelationships([])
      setSelectedItems(new Set())
    }
  }, [sourceProfileId])

  // Load target trust relationships when target profile changes
  useEffect(() => {
    if (targetProfileId) {
      loadTargetTrust()
    } else {
      setTargetTrustRelationships([])
    }
  }, [targetProfileId])

  const loadSourceTrust = async () => {
    setLoadingSource(true)
    try {
      const data = await profileService.getTrustRelationships(sourceProfileId)
      setSourceTrustRelationships(data)
      setSelectedItems(new Set())
    } catch (err) {
      console.error('Failed to load source trust relationships:', err)
      setError('Failed to load source trust relationships')
    } finally {
      setLoadingSource(false)
    }
  }

  const loadTargetTrust = async () => {
    setLoadingTarget(true)
    try {
      const data = await profileService.getTrustRelationships(targetProfileId)
      setTargetTrustRelationships(data)
    } catch (err) {
      console.error('Failed to load target trust relationships:', err)
    } finally {
      setLoadingTarget(false)
    }
  }

  const handleSelectAll = () => {
    if (selectedItems.size === sourceTrustRelationships.length) {
      setSelectedItems(new Set())
    } else {
      setSelectedItems(new Set(sourceTrustRelationships.map(t => t.id)))
    }
  }

  const handleToggleItem = (id) => {
    const newSelected = new Set(selectedItems)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedItems(newSelected)
  }

  const handleCopy = async () => {
    if (!sourceProfileId || !targetProfileId) {
      setError('Please select both source and target profiles')
      return
    }

    if (sourceProfileId === targetProfileId) {
      setError('Source and target profiles cannot be the same')
      return
    }

    if (selectedItems.size === 0) {
      setError('Please select at least one trust relationship to copy')
      return
    }

    setCopying(true)
    setError(null)
    setSuccess(null)
    setCopyResult(null)

    try {
      const categoryIds = sourceTrustRelationships
        .filter(t => selectedItems.has(t.id))
        .map(t => t.id)

      const result = await trustListService.copyTrustRelationships(
        targetProfileId,
        sourceProfileId,
        {
          category_ids: categoryIds,
          copy_all: false,
          duplicate_handling: duplicateHandling
        }
      )

      setCopyResult(result)
      setSuccess('Trust relationships copied successfully!')

      // Reload target trust relationships to show new items
      loadTargetTrust()

      // Clear selection
      setSelectedItems(new Set())
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to copy trust relationships')
    } finally {
      setCopying(false)
    }
  }

  const getTrustColor = (trust) => {
    if (trust >= 80) return 'success'
    if (trust >= 50) return 'info'
    if (trust >= 30) return 'warning'
    return 'danger'
  }

  const getProfileName = (profileId) => {
    const profile = profiles.find(p => p.id === parseInt(profileId))
    return profile?.name || `Profile ${profileId}`
  }

  const getTrustTargetName = (trust) => {
    if (trust.target_type === 'profile') {
      return getProfileName(trust.scoring_profile_id)
    } else if (trust.target_type === 'network') {
      return `Network: ${trust.network_name || trust.scoring_network_id}`
    }
    return 'Unknown'
  }

  // Check if a trust relationship already exists in target
  const isExistingInTarget = (sourceTrust) => {
    return targetTrustRelationships.some(t =>
      t.name === sourceTrust.name &&
      t.target_type === sourceTrust.target_type &&
      ((t.scoring_profile_id && t.scoring_profile_id === sourceTrust.scoring_profile_id) ||
       (t.scoring_network_id && t.scoring_network_id === sourceTrust.scoring_network_id))
    )
  }

  const availableSourceProfiles = profiles.filter(p => p.id !== parseInt(targetProfileId))
  const availableTargetProfiles = profiles.filter(p => p.id !== parseInt(sourceProfileId))

  return (
    <Container className="mt-4">
      <h2>Copy Trust Relationships</h2>
      <p className="text-muted mb-4">
        Copy trust relationships from one profile to another within your account.
      </p>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && (
        <Alert variant="success" dismissible onClose={() => setSuccess(null)}>
          {success}
          {copyResult && (
            <div className="mt-2">
              <small>
                Copied: {copyResult.copied} | Skipped: {copyResult.skipped} | Updated: {copyResult.updated}
              </small>
            </div>
          )}
        </Alert>
      )}

      <Row>
        {/* Source Profile Column */}
        <Col md={6}>
          <Card className="h-100">
            <Card.Header>
              <strong>Source Profile</strong>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Select profile to copy FROM</Form.Label>
                <Form.Select
                  value={sourceProfileId}
                  onChange={(e) => setSourceProfileId(e.target.value)}
                >
                  <option value="">Choose a profile...</option>
                  {availableSourceProfiles.map(profile => (
                    <option key={profile.id} value={profile.id}>
                      {profile.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {sourceProfileId && (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted">
                      {sourceTrustRelationships.length} trust relationship(s)
                    </small>
                    <Button
                      variant="link"
                      size="sm"
                      onClick={handleSelectAll}
                      className="p-0"
                    >
                      {selectedItems.size === sourceTrustRelationships.length ? 'Deselect All' : 'Select All'}
                    </Button>
                  </div>

                  {loadingSource ? (
                    <div className="text-center py-4">
                      <Spinner size="sm" className="me-2" />
                      Loading...
                    </div>
                  ) : sourceTrustRelationships.length === 0 ? (
                    <Alert variant="info">
                      No trust relationships in this profile.
                    </Alert>
                  ) : (
                    <ListGroup style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {sourceTrustRelationships.map((trust) => {
                        const existsInTarget = isExistingInTarget(trust)
                        return (
                          <ListGroup.Item
                            key={trust.id}
                            className="d-flex align-items-start"
                          >
                            <Form.Check
                              type="checkbox"
                              checked={selectedItems.has(trust.id)}
                              onChange={() => handleToggleItem(trust.id)}
                              className="me-3 mt-1"
                            />
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <strong>{trust.name}</strong>
                                  {existsInTarget && (
                                    <Badge bg="warning" className="ms-2" title="Already exists in target">
                                      Exists
                                    </Badge>
                                  )}
                                  <br />
                                  <small className="text-muted">
                                    {trust.target_type === 'profile' ? 'Profile' : 'Network'}: {getTrustTargetName(trust)}
                                  </small>
                                </div>
                                <Badge bg={getTrustColor(trust.trust)}>{trust.trust}%</Badge>
                              </div>
                              {trust.description && (
                                <small className="text-muted d-block mt-1">{trust.description}</small>
                              )}
                            </div>
                          </ListGroup.Item>
                        )
                      })}
                    </ListGroup>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Target Profile Column */}
        <Col md={6}>
          <Card className="h-100">
            <Card.Header>
              <strong>Target Profile</strong>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Select profile to copy TO</Form.Label>
                <Form.Select
                  value={targetProfileId}
                  onChange={(e) => setTargetProfileId(e.target.value)}
                >
                  <option value="">Choose a profile...</option>
                  {availableTargetProfiles.map(profile => (
                    <option key={profile.id} value={profile.id}>
                      {profile.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>

              {targetProfileId && (
                <>
                  <small className="text-muted d-block mb-2">
                    Current trust relationships ({targetTrustRelationships.length})
                  </small>

                  {loadingTarget ? (
                    <div className="text-center py-4">
                      <Spinner size="sm" className="me-2" />
                      Loading...
                    </div>
                  ) : targetTrustRelationships.length === 0 ? (
                    <Alert variant="info">
                      No existing trust relationships.
                    </Alert>
                  ) : (
                    <ListGroup style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {targetTrustRelationships.map((trust) => (
                        <ListGroup.Item key={trust.id}>
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <strong>{trust.name}</strong>
                              <br />
                              <small className="text-muted">
                                {trust.target_type === 'profile' ? 'Profile' : 'Network'}: {getTrustTargetName(trust)}
                              </small>
                            </div>
                            <Badge bg={getTrustColor(trust.trust)}>{trust.trust}%</Badge>
                          </div>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Copy Options */}
      <Card className="mt-4">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={6}>
              <Form.Group>
                <Form.Label>Duplicate Handling</Form.Label>
                <div>
                  <Form.Check
                    inline
                    type="radio"
                    id="duplicate-skip"
                    name="duplicateHandling"
                    label="Skip existing"
                    value="skip"
                    checked={duplicateHandling === 'skip'}
                    onChange={(e) => setDuplicateHandling(e.target.value)}
                  />
                  <Form.Check
                    inline
                    type="radio"
                    id="duplicate-update"
                    name="duplicateHandling"
                    label="Update existing"
                    value="update"
                    checked={duplicateHandling === 'update'}
                    onChange={(e) => setDuplicateHandling(e.target.value)}
                  />
                </div>
                <Form.Text className="text-muted">
                  {duplicateHandling === 'skip'
                    ? 'Existing trust relationships in the target will be left unchanged.'
                    : 'Existing trust relationships in the target will be updated with source values.'}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6} className="text-end">
              <div className="mb-2">
                <small className="text-muted">
                  {selectedItems.size} item(s) selected
                </small>
              </div>
              <Button
                variant="primary"
                size="lg"
                onClick={handleCopy}
                disabled={copying || selectedItems.size === 0 || !sourceProfileId || !targetProfileId}
              >
                {copying ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Copying...
                  </>
                ) : (
                  <>Copy Selected to {targetProfileId ? getProfileName(targetProfileId) : 'Target'}</>
                )}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </Container>
  )
}

export default CopyTrust
